import React from "react";
import Card from "react-bootstrap/Card";
import ContainerFluid from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Button from "react-bootstrap/Button";
import Skeleton from "react-loading-skeleton";

interface Tile {
  count: number;
  last_count: number;
  percentage?: number | null;
  color?: string;
}

interface Weekdata {
  week_number?: number;
  year?: number;
  start?: string;
  end?: string;
  inbound_tile: Tile;
  download_tile: Tile;
  purchase_tile: Tile;
  activation_tile: Tile;
  update_check_tile: Tile;
  update_check_unique_tile: Tile;

  pro_update_check_count?: number;
  pro_user_percentage?: number;

  xplane_version_breakdown: Record<string, number>;
  os_version_breakdown: Record<string, number>;
  plugin_version_breakdown: Record<string, number>;
}

function PercentageSummary(props: { percentages?: Record<string, number> }) {
  if (props.percentages == null) {
    return <Skeleton />;
  }

  const total = Object.values(props.percentages).reduce((sum, count) => sum + Number(count), 0);
  return (
    <span>
      {Object.entries(props.percentages)
        .slice(0, 3)
        .map(([key, count]) => `${total === 0 ? 0 : Math.round((100 * count) / total)}% ${key}`)
        .join(", ") || "-"}
    </span>
  );
}

function WeekTile(props: { title: string; tileclass: string; tile?: Tile }) {
  const color = props.tile?.color ?? "light";
  return (
    <div className={`border stats_week_tile ${props.tileclass} bg-${color}`}>
      <div className="stats_week_tile_title">{props.title}</div>
      <div className="lead">{props.tile == null ? <Skeleton /> : props.tile.count.toString()}</div>
      <div className="d-flex flex-row justify-content-between">
        {props.tile == null ? (
          <Skeleton />
        ) : (
          <>
            <div>
              <small>{props.tile.last_count.toString()}</small>
            </div>
            <div>
              <small>
                {props.tile.percentage == null
                  ? "-"
                  : props.tile.percentage > 0
                  ? `▲${props.tile.percentage.toFixed(0)}%`
                  : `▼${props.tile.percentage.toFixed(0)}%`}
              </small>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function asDate(value: unknown): Date {
  const date = value instanceof Date ? value : new Date(String(value));
  return Number.isNaN(date.getTime()) ? new Date() : date;
}

export function AdminWeek(props: { weekNumber?: number; start: Date | string; end: Date | string; weekdata?: Weekdata }) {
  const start = asDate(props.start);
  const end = asDate(props.end);
  let start_iso = start.toISOString().split("T")[0];
  let end_iso = end.toISOString().split("T")[0];
  let detail_link = `/admin/detail?from=${start_iso}&to=${end_iso}`;
  const weekNumber = props.weekNumber ?? Number(props.weekdata?.week_number ?? 0);
  const proPercentage =
    props.weekdata?.pro_user_percentage ??
    (props.weekdata?.pro_update_check_count != null && props.weekdata?.update_check_tile?.count
      ? props.weekdata.pro_update_check_count / props.weekdata.update_check_tile.count
      : undefined);

  return (
    <Card>
      <Card.Title>
        Week {weekNumber.toString()}, {start.getFullYear()} -{" "}
        {start.toLocaleDateString("en-us", {
          month: "short",
          day: "numeric",
        })}{" "}
        -{" "}
        {end.toLocaleDateString("en-us", {
          month: "short",
          day: "numeric",
        })}
        <Button style={{ float: "right" }} variant="outline-primary" href={detail_link}>
          Week details...
        </Button>
      </Card.Title>
      <ContainerFluid>
        <Row>
          <WeekTile
            title="Inbounds"
            tileclass="stats_week_tile_inbounds"
            tile={props.weekdata == undefined ? undefined : props.weekdata.inbound_tile}
          />
          <WeekTile
            title="Downloads"
            tileclass="stats_week_tile_downloads"
            tile={props.weekdata == undefined ? undefined : props.weekdata.download_tile}
          />
          <WeekTile
            title="Purchases"
            tileclass="stats_week_tile_purchases"
            tile={props.weekdata == undefined ? undefined : props.weekdata.purchase_tile}
          />
          <WeekTile
            title="Activations"
            tileclass="stats_week_tile_activations"
            tile={props.weekdata == undefined ? undefined : props.weekdata.activation_tile}
          />
          <WeekTile
            title="Update chk"
            tileclass="stats_week_tile_updatecheck"
            tile={props.weekdata == undefined ? undefined : props.weekdata.update_check_tile}
          />
          <WeekTile
            title="Up chk uniq"
            tileclass="stats_week_tile_updatecheck_unique"
            tile={props.weekdata == undefined ? undefined : props.weekdata.update_check_unique_tile}
          />
        </Row>
      </ContainerFluid>
      <div style={{ fontSize: "small", display: "inline-block" }}>
        <div>
          Pro users: {props.weekdata == undefined ? <Skeleton /> : `${((proPercentage ?? 0) * 100).toFixed(1)}%`}
        </div>
        <div>
          XP version:{" "}
          <PercentageSummary
            percentages={props.weekdata == undefined ? undefined : props.weekdata.xplane_version_breakdown}
          />
        </div>
        <div>
          Plugin version:{" "}
          <PercentageSummary
            percentages={props.weekdata == undefined ? undefined : props.weekdata.plugin_version_breakdown}
          />
        </div>
        <div>
          OS:{" "}
          <PercentageSummary
            percentages={props.weekdata == undefined ? undefined : props.weekdata.os_version_breakdown}
          />
        </div>
      </div>
    </Card>
  );
}
